import { pumpApi } from '@libs/openapi';
import dayjs from 'dayjs';
import { defineModel } from 'foca';
import type { OpenapiClientPump } from 'src/openapi/pump';

export type StepParams = OpenapiClientPump.GetTradeOhlcQuery['step'];
export type OCHLItem = OpenapiClientPump.GetTradeOhlcResponse[number];
export type ClmmOCHLItem = OpenapiClientPump.GetTradeClmmOhlcResponse[number];
export type TradeHistoryItem = OpenapiClientPump.GetTradeHistoryResponse['data'][number];

const initialState: {
  clmmOchl: Record<string, ClmmOCHLItem[]>;
  ochl: Record<string, OCHLItem[]>;
  latestPrice: Record<string, OpenapiClientPump.GetTradeLatestPriceResponse>;
  tradeHistory: Record<string, OpenapiClientPump.GetTradeHistoryResponse>;
  present: OpenapiClientPump.GetTradeTopEventsResponse;
} = {
  clmmOchl: {},
  ochl: {},
  latestPrice: {},
  tradeHistory: {},
  present: [],
};

export const pumpTradeModel = defineModel('pump-trade', {
  initialState,
  methods: {
    async fetchOCHL(opts: {
      tokenAddress: string;
      start: number;
      end?: number;
      step: StepParams;
      key: string;
      operation: 'merge' | 'replace';
    }) {
      const { tokenAddress, step, start, operation, key, end = Date.now() + 60_000 } = opts;
      const result = await pumpApi.trade.getTradeOhlc({
        query: {
          tokenAddress,
          start: dayjs(start).toISOString(),
          end: dayjs(end).toISOString(),
          step,
        },
      });
      this.setState((state) => {
        if (operation === 'replace') {
          state.ochl[key] = result;
        } else {
          const prices = (state.ochl[key] ||= []);
          result.forEach((item) => {
            const index = prices.findIndex((price) => price.timestamp === item.timestamp);
            if (index >= 0) {
              prices[index] = item;
            } else {
              prices.push(item);
            }
          });
        }
      });
      return result;
    },
    async fetchLatestPrice(tokenAddress: string) {
      const result = await pumpApi.trade.getTradeLatestPrice({ query: { tokenAddress } });
      this.setState((state) => {
        state.latestPrice[tokenAddress] = result;
      });
    },
    async fetchClmmOCHL(opts: {
      clmmPoolId: string;
      start: number;
      step: StepParams;
      key: string;
      operation: 'merge' | 'replace';
    }) {
      const { clmmPoolId, step, start, operation, key } = opts;
      const result = await pumpApi.trade.getTradeClmmOhlc({
        query: {
          clmmPoolId,
          start: dayjs(start).toISOString(),
          end: dayjs(Date.now() + 60_000).toISOString(),
          step,
        },
      });
      this.setState((state) => {
        if (operation === 'replace') {
          state.clmmOchl[key] = result;
        } else {
          const prices = (state.clmmOchl[key] ||= []);
          result.forEach((item) => {
            const index = prices.findIndex((price) => price.timestamp === item.timestamp);
            if (index >= 0) {
              prices[index] = item;
            } else {
              prices.push(item);
            }
          });
        }
      });
      return result;
    },
    async fetchTradeHistory(
      tokenAddress: string,
      page: number = 10,
      address: string = '',
      isBuy?: boolean,
    ) {
      const result = await pumpApi.trade.getTradeHistory({
        query: { page, address, tokenAddress, isBuy },
      });
      this.setState((state) => {
        state.tradeHistory[tokenAddress] = result;
      });
      return result;
    },
    async fetchTradePresent() {
      const result = await pumpApi.trade.getTradeTopEvents({
        query: { size: 20 },
      });
      this.setState((state) => {
        state.present = result;
      });
      return result;
    },
  },
});
